import { AdminPermissions } from '@zspace/roles';
import {
  Account,
  Criteria,
  DeferredResponse,
  PaginatedAPIResponse,
  SortDirection,
} from '@zspace/types';
import { Suspense, useCallback, useMemo, useState } from 'react';
import {
  defer,
  LoaderFunction,
  useAsyncValue,
  useLoaderData,
} from 'react-router-dom';
import { AdminProvider } from '../../shared/context/admin-context';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import useHttpRequest from '../../shared/hooks/http-request';
import ProtectedPage from '../../shared/protected-page/protected-page';
import BoxLayout from '../../ui/box-layout/box-layout';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import AdminHeader from '../admin-header/admin-header';
import AdminInboxTable, {
  AdminToolsView,
} from '../admin-inbox-table/admin-inbox-table';
import { fetchAccounts as fetchAccountsRequest } from '../admin-service';
import { adminSalesOrdersTableColumns } from '../admin-table/admin-table-columns';

const initialCriteria: Criteria = {
  itemsPerPage: 10,
  pageNumber: 1,
  search: '',
  sortBy: '',
  sortDirection: SortDirection.ASC,
};

const FETCH_ACCOUNTS_ERROR_MESSAGE =
  'Accounts could not be fetched. Please try again';
const FETCH_SALES_ORDERS_BY_ACCOUNT_ERROR_MESSAGE =
  'Sales orders could not be fetched. Please try again';
const DEFAULT_INBOX_TABLE_TEXT =
  'Please select an account to display sales orders';
const TABLE_EMPTY_TEXT = 'There are no sales orders at the moment';
const INBOX_EMPTY_TEXT = 'There are no accounts at the moment';

export const loader: LoaderFunction = () => {
  const response = fetchAccountsRequest(initialCriteria);

  return defer({ response });
};

export function AdminSalesOrdersPageContent() {
  const accountsInitialResponse =
    useAsyncValue() as PaginatedAPIResponse<Account>;
  const [accounts, setAccounts] = useState<Account[]>(
    accountsInitialResponse.data
  );
  const [accountsDataResponse, setAccountsDataResponse] = useState<
    PaginatedAPIResponse<Account>
  >(accountsInitialResponse);
  const [accountsCriteria, setAccountsCriteria] =
    useState<Criteria>(initialCriteria);
  const {
    executeHttpRequest: executeFetchAccountsRequest,
    isLoading: loadingInitialAccounts,
  } = useHttpRequest();
  const { executeHttpRequest: executeFetchMoreAccountsRequest } =
    useHttpRequest();

  const hasMoreAccounts = useMemo(
    () => !!accountsDataResponse.hasMore,
    [accountsDataResponse]
  );

  const fetchAccounts = useCallback(
    async (criteria: Criteria) =>
      await executeFetchAccountsRequest({
        asyncFunction: async () => {
          const response = await fetchAccountsRequest(criteria);
          setAccounts(response.data);
          setAccountsDataResponse(response);
        },
        customErrorMessage: FETCH_ACCOUNTS_ERROR_MESSAGE,
      }),
    [executeFetchAccountsRequest]
  );

  const fetchMoreAccounts = useCallback(
    () =>
      executeFetchMoreAccountsRequest({
        asyncFunction: async () => {
          const nextPage = accountsCriteria.pageNumber + 1;
          const updatedCriteria = {
            ...accountsCriteria,
            pageNumber: nextPage,
          };
          const response = await fetchAccountsRequest(updatedCriteria);
          setAccountsCriteria(updatedCriteria);
          setAccounts((prevAccounts) => prevAccounts.concat(response.data));
          setAccountsDataResponse(response);
        },
        customErrorMessage: FETCH_ACCOUNTS_ERROR_MESSAGE,
      }),
    [accountsCriteria, executeFetchMoreAccountsRequest]
  );

  const handleTableFilterSubmit = useCallback(
    async (updatedAccountsCriteria: Criteria) => {
      await fetchAccounts(updatedAccountsCriteria);
    },
    [fetchAccounts]
  );

  const onSearchChange = useCallback(
    (search: string) => {
      const updatedCriteria = {
        ...initialCriteria,
        search,
      };
      setAccountsCriteria(updatedCriteria);
      handleTableFilterSubmit(updatedCriteria);
    },
    [handleTableFilterSubmit]
  );

  return (
    <BoxLayout
      className="is-min-height-80 mx-10 my-4 mb-4"
      header={
        <AdminHeader
          title="Sales Orders"
          search={accountsCriteria.search}
          onSearchChange={onSearchChange}
        />
      }
    >
      <AdminInboxTable
        fetchMoreInboxItems={fetchMoreAccounts}
        hasMoreInboxItems={hasMoreAccounts}
        inboxItems={accounts}
        loadingInitialInboxItems={loadingInitialAccounts}
        tableColumns={adminSalesOrdersTableColumns}
        searchValue={accountsCriteria.search}
        initialCriteriaConfig={{
          sortBy: 'number',
          sortDirection: SortDirection.DES,
        }}
      />
    </BoxLayout>
  );
}

export function AdminSalesOrdersPage() {
  const { response } = useLoaderData() as DeferredResponse<
    Promise<PaginatedAPIResponse<Account>>
  >;

  return (
    <ProtectedPage permissions={AdminPermissions.ADMIN_TOOLS_ACCESS}>
      <Suspense fallback={<PageSpinner />}>
        <ErrorHandlingAwait resolve={response}>
          <AdminProvider
            adminView={AdminToolsView.SALES_ORDERS}
            fetchTableRecordsErrorMessage={
              FETCH_SALES_ORDERS_BY_ACCOUNT_ERROR_MESSAGE
            }
            defaultInboxTableText={DEFAULT_INBOX_TABLE_TEXT}
            tableEmptyText={TABLE_EMPTY_TEXT}
            inboxEmptyText={INBOX_EMPTY_TEXT}
          >
            <AdminSalesOrdersPageContent />
          </AdminProvider>
        </ErrorHandlingAwait>
      </Suspense>
    </ProtectedPage>
  );
}

export default AdminSalesOrdersPage;
